/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.14.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';

import {
    loadTableFieldsSuccessful,
    loadTableListAction,
    loadTableListError,
    loadTableListSuccessful,
    setTableCurrentName,
    tableResetAction,
} from '../../components/Table/actions/tableActions';
import { TABLE_DENSITY_LOAD_SIZE, TABLE_LOAD_SIZE } from '../../components/Table/constants';
import { TABLE_ROW_DELETE_ACTION, TABLE_ROW_RESTORE_ACTION } from '../../components/Table/constants/actionConstants';
import {
    makeSelectorTableCurrentName,
    makeSelectorTableData,
    makeSelectorTableRemovedRowIndexes,
} from '../../components/Table/selectors/tableSelectors';
import {
    TableNameType,
    TLoadTableListAction,
    TTableRowDeleteAction,
    TTableRowRestoreAction,
} from '../../components/Table/types/tableTypes';
import {
    API_HOSTNAME,
    IMPORT_PERSONAL_DATA_LIST,
    IMPORT_PERSONAL_DATA_UPLOAD_DOCUMENT,
    WIZARD,
    WIZARD_ACTION,
} from '../../modules/app/Api';
import { EHttpMethod, HttpStatus } from '../../modules/app/appConstants';
import { LOAD_INTERNAL_TRANSLATIONS_SUCCESS } from '../../modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardImportPersonalData,
} from '../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../modules/internationalization/types';
import { IWizardImportPersonalDataTranslation } from '../../modules/internationalization/types/wizards/importPersonalData';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { modalOpen } from '../../modules/modal/modalActions';
import { IModalRenderProps } from '../../modules/modal/types/modalTypes';
import { makeSelectIsDensity } from '../../modules/storage/storageSelectors';
import { wizardReportStorageRestoreSaga } from '../../modules/wizard/components/WizardReport/wizardReportSagas';
import { makeSelectWizardReportOrderParams } from '../../modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportOrderParams } from '../../modules/wizard/components/WizardReport/wizardReportTypes';
import {
    wizardNextAction,
    wizardOpenModalSummaryAction,
    wizardStorageSaveStepAction,
    wizardStorageUpdateStepAction,
} from '../../modules/wizard/wizardActions';
import {
    EWizardName,
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '../../modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '../../modules/wizard/wizardSagas';
import {
    makeSelectWizardBasketSnapshotHeader,
    makeSelectWizardBasketSnapshotId,
    makeSelectWizardStepName,
} from '../../modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '../../modules/wizard/wizardTypes';
import { requestSaga } from '../../sagas/requestSaga';
import { formQueryParamsString } from '../../sagas/utils/formQueryParamsString';
import { stringFormat } from '../../utils/stringFormat';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectIsFeatureWithVersionEnabled,
} from '../PageAuth/selectors/authSelectors';
import {
    formImportPersonalDataTableFields,
    formImportPersonalDataTableFieldsFinalStep,
    formImportPersonalDataTableList,
    formImportPersonalDataTableListFinalStep,
} from './components/WizardImportPersonalDataTable/WizardImportPersonalDataTableCells';
import { TWizardImportPersonalDataAttachFile } from './types/wizardImportPersonalDataActionTypes';
import { IWizardImportPersonalDataValidatedSubscriberResponse } from './types/wizardImportPersonalDataTypes';
import {
    wizardImportPersonalDataAttachFileFailed,
    wizardImportPersonalDataAttachFileSuccessful,
    wizardImportPersonalDataInitValidatedListFailed,
    wizardImportPersonalDataSetParamsAction,
    wizardImportPersonalDataSetStepValidAction,
    wizardImportPersonalDataStartValidateAction,
} from './wizardImportPersonalDataActions';
import {
    EWizardImportPersonalDataSteps,
    WIZARD_IMPORT_PERSONAL_DATA_ATTACH_FILE,
    WIZARD_IMPORT_PERSONAL_DATA_ATTACH_FILE_FAILED,
    WIZARD_IMPORT_PERSONAL_DATA_HANDLE_NEXT,
    WIZARD_IMPORT_PERSONAL_DATA_INIT_VALIDATED_LIST,
    WIZARD_IMPORT_PERSONAL_DATA_INIT_VALIDATED_LIST_FAILED,
    WIZARD_IMPORT_PERSONAL_DATA_SET_PARAMS,
    WIZARD_IMPORT_PERSONAL_DATA_START_VALIDATE,
} from './wizardImportPersonalDataConstant';
import {
    makeSelectWizardImportPersonalDataErrorRowsAmount,
    makeSelectWizardImportPersonalDataFileName,
    makeSelectWizardImportPersonalDataFileRowsAmount,
    makeSelectWizardImportPersonalDataMaxImportSubscriberAmount,
    makeSelectWizardImportPersonalDataRemovedRowsAmount,
} from './wizardImportPersonalDataSelectors';

/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */
export function* wizardImportPersonalDataMetaSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    yield call(wizardImportPersonalDataLoadDataFromStorageSaga);

    switch (stepName) {
        case EWizardImportPersonalDataSteps.DocumentSelectionStep:
            const fileTemplateUrl = action.payload.step.fileTemplateUrl;
            const maxImportSubscriberAmount: number = action.payload.step.maxImportSubscriberAmount;
            yield put(
                wizardImportPersonalDataSetParamsAction({
                    fileTemplateUrl,
                    maxImportSubscriberAmount,
                }),
            );
            break;
        case EWizardImportPersonalDataSteps.ContentValidationStep:
            yield put(setTableCurrentName(TableNameType.importPersonalData));
            yield call(importPersonalDataFormTableFieldsSaga);
            break;
        case EWizardImportPersonalDataSteps.ConfirmImportStep:
            const errorRowsAmount = action.payload.step.errorRowsAmount;
            yield put(
                wizardImportPersonalDataSetParamsAction({
                    errorRowsAmount,
                }),
            );
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */
export function* wizardImportPersonalDataHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardImportPersonalDataSteps.DocumentSelectionStep:
        case EWizardImportPersonalDataSteps.ContentValidationStep:
            yield put(wizardNextAction({}));
            break;
        case EWizardImportPersonalDataSteps.ConfirmImportStep:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());
            const list = yield select(makeSelectorTableData());
            const removedRowIndexes: number[] = yield select(makeSelectorTableRemovedRowIndexes());

            const params: { [key: string]: number[] } = {
                excludedSubsIds: removedRowIndexes?.map((index) => list[index]?.subsId).filter((id) => id),
            };

            yield put(
                wizardNextAction({
                    data: params,
                    ...reportParams,
                }),
            );
            break;
    }

    yield call(wizardImportPersonalDataUpdateStorageSaga);
}

export function* wizardImportPersonalDataHandleNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardImportPersonalDataSteps.DocumentSelectionStep:
        case EWizardImportPersonalDataSteps.ContentValidationStep:
            break;
        case EWizardImportPersonalDataSteps.ConfirmImportStep:
            const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';

            const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
                makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
            );

            if (isMegaPowersFeatureGreaterOrEqualThan2) {
                const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

                const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
                const goBack = () => breadCrumbGoBack(breadCrumbsData);

                yield put(
                    modalOpen({
                        render: () =>
                            createElement(ModalMultipleOrders, {
                                isOpen: true,
                                orderOrOrders: orderId,
                                onClose: goBack,
                                onSuccessButtonClick: breadCrumbGoBack,
                                orderTypeTranlation: orderCreationModal.success.orderType.PERSONAL_DATA_IMPORT,
                            }),
                    }),
                );
            } else {
                yield put(wizardOpenModalSummaryAction({ orderId }));
            }
            break;
    }
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardImportPersonalDataUpdateStorageSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    const errorRowsAmount = yield select(makeSelectWizardImportPersonalDataErrorRowsAmount());
    const fileName = yield select(makeSelectWizardImportPersonalDataFileName());
    const fileRowsAmount = yield select(makeSelectWizardImportPersonalDataFileRowsAmount());
    const maxImportSubscriberAmount = yield select(makeSelectWizardImportPersonalDataMaxImportSubscriberAmount());

    switch (stepName) {
        case EWizardImportPersonalDataSteps.DocumentSelectionStep:
        case EWizardImportPersonalDataSteps.ContentValidationStep:
        case EWizardImportPersonalDataSteps.ConfirmImportStep:
            yield put(
                wizardStorageUpdateStepAction({
                    errorRowsAmount,
                    fileName,
                    fileRowsAmount,
                    maxImportSubscriberAmount,
                }),
            );
            break;
    }
}

export function* wizardImportPersonalDataLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    /**
     * Если обновили страницу обновим всю информацию, которая у нас есть
     */
    if (localStorageParams) {
        yield put(
            wizardImportPersonalDataSetParamsAction({
                errorRowsAmount: localStorageParams.errorRowsAmount,
                fileName: localStorageParams.fileName,
                fileRowsAmount: localStorageParams.fileRowsAmount,
                maxImportSubscriberAmount: localStorageParams.maxImportSubscriberAmount,
            }),
        );
    }

    yield call(wizardImportPersonalDataUpdateStorageSaga);
}

export function* wizardImportPersonalDataStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

export function* wizardImportPersonalDataValidateSaga(): SagaIterator {
    let isStepValid = true;
    let validateError = '';
    const stepName = yield select(makeSelectWizardStepName());
    const errorRowsAmount = yield select(makeSelectWizardImportPersonalDataErrorRowsAmount());
    const fileName = yield select(makeSelectWizardImportPersonalDataFileName());
    const fileRowsAmount = yield select(makeSelectWizardImportPersonalDataFileRowsAmount());
    const removedRowsAmount = yield select(makeSelectWizardImportPersonalDataRemovedRowsAmount());

    switch (stepName) {
        case EWizardImportPersonalDataSteps.DocumentSelectionStep:
            if (!fileName) {
                isStepValid = false;
            }
            break;
        case EWizardImportPersonalDataSteps.ContentValidationStep:
            break;
        case EWizardImportPersonalDataSteps.ConfirmImportStep:
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }
            isStepValid = fileRowsAmount - errorRowsAmount - removedRowsAmount > 0;
            break;
    }
    yield put(
        wizardImportPersonalDataSetStepValidAction({
            isStepValid,
            validateError,
        }),
    );
}

export function* wizardImportPersonalDataAttachFileSaga(action: TWizardImportPersonalDataAttachFile): SagaIterator {
    const payload = action.payload;

    try {
        const options = {
            headers: {
                'accept': 'application/json;charset=UTF-8',
                'X-NODE': 'sticky',
            },
        };
        const data = new FormData();
        data.append('file', payload.file);
        const url =
            API_HOSTNAME +
            stringFormat(WIZARD, EWizardName.IMPORT_PERSONAL_DATA) +
            stringFormat(WIZARD_ACTION, IMPORT_PERSONAL_DATA_UPLOAD_DOCUMENT);
        const { fileRowsAmount, resultFileId } = yield call(requestSaga, url, EHttpMethod.POST, data, options);

        yield put(
            wizardImportPersonalDataAttachFileSuccessful({
                fileName: payload.file.name,
                fileRowsAmount,
                resultFileId,
            }),
        );

        yield put(wizardImportPersonalDataStartValidateAction());
        yield put(tableResetAction({ tableName: TableNameType.importPersonalData }));
        yield put(wizardImportPersonalDataSetParamsAction({ removedRowsAmount: 0 }));
    } catch (e) {
        yield put(wizardImportPersonalDataAttachFileFailed(e as string));
    }
}

export function* importPersonalDataFormTableFieldsSaga(): SagaIterator {
    let translationImportPersonalData: IWizardImportPersonalDataTranslation = yield select(
        makeSelectTranslationWizardImportPersonalData(),
    );
    if (translationImportPersonalData.title === '') {
        yield take(LOAD_INTERNAL_TRANSLATIONS_SUCCESS);
        translationImportPersonalData = yield select(makeSelectTranslationWizardImportPersonalData());
    }
    const stepName = yield select(makeSelectWizardStepName());
    const importPersonalDataFields =
        stepName === EWizardImportPersonalDataSteps.ConfirmImportStep
            ? formImportPersonalDataTableFieldsFinalStep(translationImportPersonalData.table)
            : formImportPersonalDataTableFields(translationImportPersonalData.table);
    yield put(loadTableFieldsSuccessful({ fields: importPersonalDataFields }));
}

export function* importPersonalDataLoadListSaga(action: TLoadTableListAction): SagaIterator {
    const start = action.payload?.force ?? false;
    const header: string = yield select(makeSelectWizardBasketSnapshotHeader());
    const isDensity: boolean = yield select(makeSelectIsDensity());
    const tableName: TableNameType = yield select(makeSelectorTableCurrentName());
    const list = yield select(makeSelectorTableData());
    const tableLoadSize = isDensity ? TABLE_DENSITY_LOAD_SIZE : TABLE_LOAD_SIZE;

    const stepName = yield select(makeSelectWizardStepName());
    const fileRowsAmount = yield select(makeSelectWizardImportPersonalDataFileRowsAmount());
    const translationImportPersonalData: IWizardImportPersonalDataTranslation = yield select(
        makeSelectTranslationWizardImportPersonalData(),
    );

    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-NODE': 'sticky',
            'X-BASKET-SNAPSHOT': header,
        },
    };

    try {
        const url =
            API_HOSTNAME +
            stringFormat(WIZARD, EWizardName.IMPORT_PERSONAL_DATA) +
            stringFormat(WIZARD_ACTION, IMPORT_PERSONAL_DATA_LIST);
        const queryString = formQueryParamsString({
            from: start ? 0 : list.length,
            size: tableLoadSize,
        });
        const tableData: { fileData: IWizardImportPersonalDataValidatedSubscriberResponse[] } = yield call(
            requestSaga,
            url + queryString,
            EHttpMethod.GET,
            null,
            options,
        );

        const formattedData =
            stepName === EWizardImportPersonalDataSteps.ConfirmImportStep
                ? formImportPersonalDataTableListFinalStep(
                      tableData.fileData,
                      translationImportPersonalData[EWizardImportPersonalDataSteps.ConfirmImportStep].status,
                  )
                : formImportPersonalDataTableList(tableData.fileData);

        yield put(
            loadTableListSuccessful({
                tableName: tableName,
                data: [...(start ? [] : list), ...formattedData],
                count: fileRowsAmount,
                basketAllIn: false,
                tableFooter: undefined,
            }),
        );
    } catch (e: any) {
        yield put(loadTableListError({ tableName: tableName, error: e.toString() }));
        yield put(wizardImportPersonalDataInitValidatedListFailed(e));
    }
}

export function* wizardImportPersonalDataInitValidatedListSaga() {
    yield put(setTableCurrentName(TableNameType.importPersonalData));
    yield call(importPersonalDataFormTableFieldsSaga);
    yield put(loadTableListAction({ force: true }));
}

export function* removeRowFromTableInConfirmationStepSaga(action: TTableRowDeleteAction): SagaIterator {
    const list = yield select(makeSelectorTableData());
    const errorRowsAmount = yield select(makeSelectWizardImportPersonalDataErrorRowsAmount());
    const removedRowsAmount = yield select(makeSelectWizardImportPersonalDataRemovedRowsAmount());

    const data = list[action.payload?.index];

    yield put(
        wizardImportPersonalDataSetParamsAction({
            errorRowsAmount: data?.errors?.length > 0 ? errorRowsAmount - 1 : errorRowsAmount,
            removedRowsAmount: removedRowsAmount + 1,
        }),
    );
}

export function* restoreRowFromTableInConfirmationStepSaga(action: TTableRowRestoreAction): SagaIterator {
    const list = yield select(makeSelectorTableData());
    const errorRowsAmount = yield select(makeSelectWizardImportPersonalDataErrorRowsAmount());
    const removedRowsAmount = yield select(makeSelectWizardImportPersonalDataRemovedRowsAmount());

    const data = list[action.payload?.index];

    yield put(
        wizardImportPersonalDataSetParamsAction({
            errorRowsAmount: data?.errors?.length > 0 ? errorRowsAmount + 1 : errorRowsAmount,
            removedRowsAmount: removedRowsAmount - 1,
        }),
    );
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */
export function* wizardImportPersonalDataFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationImportPersonalData = yield select(makeSelectTranslationWizardImportPersonalData());
        if (
            translationImportPersonalData[EWizardImportPersonalDataSteps.DocumentSelectionStep].attachError.codes[
                errorCode
            ]
        ) {
            yield put(wizardImportPersonalDataSetParamsAction({ fileName: '', errorCode }));
            return;
        } else {
            title = translationImportPersonalData.error.codes[errorCode] || translationCommon.error.codes[errorCode];
        }
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

/*
 * -------------------------------------------
 *       Wizard Sagas
 * -------------------------------------------
 */
export function* wizardImportPersonalDataSagas() {
    yield all([
        // initialization
        takeEvery(WIZARD_START_SUCCESS, wizardImportPersonalDataStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardImportPersonalDataMetaSaga),
        // next
        takeEvery(WIZARD_IMPORT_PERSONAL_DATA_HANDLE_NEXT, wizardImportPersonalDataHandleNextSaga),
        takeEvery([WIZARD_NEXT_SUCCESS, WIZARD_BACK_SUCCESS], wizardImportPersonalDataLoadDataFromStorageSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, wizardImportPersonalDataHandleNextSuccessSaga),
        // validate
        takeEvery(
            [WIZARD_IMPORT_PERSONAL_DATA_START_VALIDATE, WIZARD_IMPORT_PERSONAL_DATA_SET_PARAMS],
            wizardImportPersonalDataValidateSaga,
        ),
        takeEvery(WIZARD_IMPORT_PERSONAL_DATA_SET_PARAMS, wizardImportPersonalDataUpdateStorageSaga),
        takeEvery(WIZARD_IMPORT_PERSONAL_DATA_ATTACH_FILE, wizardImportPersonalDataAttachFileSaga),
        takeEvery(WIZARD_IMPORT_PERSONAL_DATA_INIT_VALIDATED_LIST, wizardImportPersonalDataInitValidatedListSaga),
        //remove row
        takeEvery(TABLE_ROW_DELETE_ACTION, removeRowFromTableInConfirmationStepSaga),
        takeEvery(TABLE_ROW_RESTORE_ACTION, restoreRowFromTableInConfirmationStepSaga),
        // failed
        takeEvery(
            [
                WIZARD_BACK_FAILED,
                WIZARD_NEXT_FAILED,
                WIZARD_RESTORE_FAILED,
                WIZARD_START_FAILED,
                WIZARD_IMPORT_PERSONAL_DATA_ATTACH_FILE_FAILED,
                WIZARD_IMPORT_PERSONAL_DATA_INIT_VALIDATED_LIST_FAILED,
            ],
            wizardImportPersonalDataFailedSaga,
        ),
    ]);
}
